import { Text } from '@grammarly/design-system'
import classes from './LoadingText.module.scss'
import { useSimulatedLoadingSteps } from '../hooks/useSimulatedLoadingSteps'

export interface LoadingTextProps {
  readonly steps: readonly string[]
  simulateStepLoading?: boolean
  /**
   * If not provided, defaults to steps.length*1000
   */
  simulatedLoadingTime?: number
}

/**
 * Component for rendering a series of 'steps' in text form with a shimmer
 * effect, as content is loaded.
 * If 'simulateStepLoading' is true, it progressively adds steps to display,
 * over the 'simulatedLoadingTime' timespan
 */
export const LoadingText = ({
  simulatedLoadingTime,
  simulateStepLoading,
  steps,
}: LoadingTextProps) => {
  if (simulateStepLoading) {
    return <WithSimulatedLoadingSteps steps={steps} simulatedLoadingTime={simulatedLoadingTime} />
  }

  return <Content steps={steps} />
}

// Helper components so we can get a 'conditional hook'
const WithSimulatedLoadingSteps = ({
  steps,
  simulatedLoadingTime,
}: Omit<LoadingTextProps, 'simulateStepLoading'>) => {
  const loadedSteps = useSimulatedLoadingSteps(steps, { totalTime: simulatedLoadingTime })
  return <Content steps={loadedSteps} />
}

const Content = ({
  steps,
}: Omit<LoadingTextProps, 'simulateStepLoading' | 'simulatedLoadingTime'>) => {
  return (
    <div className={classes.root}>
      {steps.map(step => (
        <div key={step} className={classes.step}>
          <div className={classes.decorator}>
            <div className={classes.topBar} />
            <div className={classes.dot} />
            <div className={classes.bottomBar} />
          </div>
          <Text className={classes.text} as={'span'} variant={'text-small'}>
            {step}
          </Text>
        </div>
      ))}
    </div>
  )
}
